import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_HARNESS_ID = '';

function buildId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeError(error) {
  if (!error) {
    return 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

function normalizeHistoryEntry(entry, index) {
  const message = entry.message || entry.error || '';
  const owner = entry.owner === 'user' ? 'user' : 'agent';
  const state = entry.error ? 'error' : 'complete';

  return {
    id: entry.id || entry.message_id || `history-${index}`,
    kind: entry.error ? 'error' : 'message',
    message,
    messageId: entry.message_id,
    owner,
    state,
  };
}

function appendToMessage(entries, messageId, message) {
  return entries.map((entry) => {
    if (entry.messageId !== messageId || entry.kind !== 'message') {
      return entry;
    }

    return {
      ...entry,
      message: `${entry.message}${message || ''}`,
    };
  });
}

function completeMessage(entries, messageId, message) {
  return entries.map((entry) => {
    if (entry.messageId !== messageId || entry.kind !== 'message') {
      return entry;
    }

    return {
      ...entry,
      message: message || entry.message,
      state: 'complete',
    };
  });
}

function addAgentPlaceholder(entries, messageId) {
  if (!messageId) {
    return entries;
  }

  if (entries.some((entry) => entry.messageId === messageId)) {
    return entries;
  }

  return [
    ...entries,
    {
      id: messageId || buildId('agent'),
      kind: 'message',
      message: '',
      messageId,
      owner: 'agent',
      state: 'streaming',
    },
  ];
}

export function useHarnessChat(ai) {
  const location = useLocation();
  const [harnessId, setHarnessId] = useState(DEFAULT_HARNESS_ID);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const clientTools = useMemo(
    () => ({
      get_browser: () => ({
        language: navigator.language,
        url: `${location.pathname}${location.search}${location.hash}`,
        userAgent: navigator.userAgent,
        viewport: {
          height: window.innerHeight,
          width: window.innerWidth,
        },
      }),
    }),
    [location.hash, location.pathname, location.search],
  );

  const loadSessions = useCallback(async () => {
    if (!ai?.chatList || !harnessId) {
      setSessions([]);
      return;
    }

    setIsLoadingSessions(true);
    setError(null);

    try {
      const response = await ai.chatList(harnessId);
      setSessions(Array.isArray(response) ? response : response?.chats || response?.sessions || []);
    } catch (e) {
      setError(normalizeError(e));
    } finally {
      setIsLoadingSessions(false);
    }
  }, [ai, harnessId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const startNewChat = useCallback(() => {
    setCurrentChatId(null);
    setTranscript([]);
    setError(null);
  }, []);

  const selectHarness = useCallback((nextHarnessId) => {
    setHarnessId(nextHarnessId);
    setCurrentChatId(null);
    setSessions([]);
    setTranscript([]);
    setError(null);
  }, []);

  const loadChat = useCallback(
    async (chatId) => {
      if (!ai?.chatRetrieve || !chatId) {
        return;
      }

      setIsLoadingChat(true);
      setError(null);

      try {
        const response = await ai.chatRetrieve(chatId);
        const entries = Array.isArray(response) ? response : response?.history || response?.messages || [];

        setCurrentChatId(chatId);
        setTranscript(entries.map(normalizeHistoryEntry));
      } catch (e) {
        setError(normalizeError(e));
      } finally {
        setIsLoadingChat(false);
      }
    },
    [ai],
  );

  const sendMessage = useCallback(
    async (message) => {
      const cleanMessage = message.trim();

      if (!ai?.chat || !harnessId || !cleanMessage || isSending) {
        return;
      }

      setIsSending(true);
      setError(null);
      setTranscript((entries) => [
        ...entries,
        {
          id: buildId('user'),
          kind: 'message',
          message: cleanMessage,
          owner: 'user',
          state: 'complete',
        },
      ]);

      try {
        const reply = await ai.chat(harnessId, cleanMessage, {
          chatId: currentChatId,
          clientTools,
          onChunk: (payload) => {
            const messageId = payload?.message_id;
            setTranscript((entries) =>
              appendToMessage(addAgentPlaceholder(entries, messageId), messageId, payload?.message),
            );
          },
          onError: (payload) => {
            const messageId = payload?.message_id || buildId('error');
            const payloadError = payload?.error || payload;
            const messageText = normalizeError(payloadError);

            setTranscript((entries) => [
              ...entries,
              {
                id: messageId,
                kind: 'error',
                message: messageText,
                messageId,
                owner: 'agent',
                state: 'error',
              },
            ]);
            setError(messageText);
          },
          onResponse: (payload) => {
            const messageId = payload?.message_id;
            setTranscript((entries) =>
              completeMessage(addAgentPlaceholder(entries, messageId), messageId, payload?.message),
            );
          },
          onToolCall: (payload) => {
            setTranscript((entries) => [
              ...entries,
              {
                id: buildId('tool'),
                kind: 'tool',
                message: payload?.name || payload?.tool || 'client tool',
                messageId: payload?.message_id,
                owner: 'agent',
                state: 'tool',
              },
            ]);
          },
        });

        if (reply?.chat_id) {
          setCurrentChatId(reply.chat_id);
        }

        if (reply?.message_id) {
          setTranscript((entries) => addAgentPlaceholder(entries, reply.message_id));
        }

        loadSessions();
      } catch (e) {
        const messageText = normalizeError(e);
        setError(messageText);
        setTranscript((entries) => [
          ...entries,
          {
            id: buildId('error'),
            kind: 'error',
            message: messageText,
            owner: 'agent',
            state: 'error',
          },
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [ai, clientTools, currentChatId, harnessId, isSending, loadSessions],
  );

  return {
    currentChatId,
    error,
    harnessId,
    isLoadingChat,
    isLoadingSessions,
    isSending,
    loadChat,
    refreshSessions: loadSessions,
    sendMessage,
    sessions,
    setHarnessId: selectHarness,
    startNewChat,
    transcript,
  };
}
